import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  combineLatest,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  catchError,
  tap
} from 'rxjs';
import { DynamicCrmService } from '../../modules/services/dynamic-crm.service';
import * as dynamicCRMModels from '../../modules/models/dynamic-crm-models';
import { hasRichTextContent, sanitizeTrustedHtml } from '../../modules/shared/ckeditor/utils/rich-text.utils';

@Component({
  selector: 'app-trade-details-tasks',
  templateUrl: './trade-details-tasks.component.html',
  styleUrls: ['./trade-details-tasks.component.scss'],
  standalone: false
})
export class TradeDetailsTasksComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() partyDetails: { partyId: string; partyType: string; clientName?: string }[] = [];

  @ViewChildren('taskBodyEl') taskBodyEls?: QueryList<ElementRef<HTMLElement>>;
  private renderedTasks: dynamicCRMModels.ICrmTask[] = [];
  @Output() tasksCountChange = new EventEmitter<number>();

  readonly taskPriorityOptions = [
    { value: 0, label: 'Low' },
    { value: 1, label: 'Normal' },
    { value: 2, label: 'High' }
  ];

  readonly taskStatusOptions = [
    { value: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.OPEN, label: 'Open' },
    { value: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED, label: 'Not Started' },
    { value: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.IN_PROGRESS, label: 'In Progress' },
    { value: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.WAITING, label: 'Waiting on Someone Else' },
    { value: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.DEFERRED, label: 'Deferred' }
  ];

  readonly taskTimeRangeOptions: Array<{ value: dynamicCRMModels.TaskTimeRange; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'immediate', label: 'Next 7 Days' },
    { value: 'future', label: 'Future' },
    { value: 'past', label: 'Past' }
  ];

  createTaskForm = new FormGroup({
    taskSubject: new FormControl<string>('', Validators.required),
    taskDescription: new FormControl<string>('', (control: AbstractControl): ValidationErrors | null => {
      return hasRichTextContent(control.value || '') ? null : { required: true };
    }),
    taskDueDate: new FormControl<Date | null>(null, Validators.required),
    taskDueTime: new FormControl<string>('08:00', Validators.required),
    taskPriority: new FormControl<number>(1, Validators.required),
    taskDuration: new FormControl<number>(30, Validators.required),
    taskStatus: new FormControl<number>(dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED, Validators.required),
    taskSendEmail: new FormControl<boolean>(false)
  });

  readonly tasksFetching$ = new BehaviorSubject<boolean>(false);
  readonly isDownloadingAttachment$ = new BehaviorSubject<boolean>(false);
  readonly taskSortOrder$ = new BehaviorSubject<dynamicCRMModels.TaskSortOrder>('desc');
  readonly taskTimeRange$ = new BehaviorSubject<dynamicCRMModels.TaskTimeRange>('all');
  private readonly profileIds$ = new BehaviorSubject<string[]>([]);
  private hasResolvedProfiles = false;

  isCreateTaskOpen = false;
  isSubmittingTask = false;
  hasRequestedTasks = false;
  private hasPendingPartyRefresh = false;
  closingTaskId = '';

  tasksErrorMessage = '';
  taskAttachmentErrorMessage = '';
  createTaskErrorMessage = '';
  createTaskSuccessMessage = '';

  selectedTaskAttachmentFile: File | null = null;
  selectedTaskAttachmentName = '';
  selectedTaskAttachmentSize = 0;

  readonly tasksFromCrm$: Observable<dynamicCRMModels.ICrmTask[]> = combineLatest([
    this.profileIds$,
    this.taskSortOrder$,
    this.taskTimeRange$
  ]).pipe(
    switchMap(([profileIds, sortOrder, timeRange]) => {
      this.tasksErrorMessage = '';
      if (!profileIds.length) {
        if (!this.hasResolvedProfiles) {
          return EMPTY;
        }
        return of([]);
      }

      this.tasksFetching$.next(true);
      return this.dynamiccrmSvc.getTasksByProfiles({
        profileIds,
        sortOrder,
        timeRange
      }).pipe(
        catchError(() => {
          this.tasksErrorMessage = 'Unable to load tasks.';
          return of([]);
        }),
        finalize(() => this.tasksFetching$.next(false))
      );
    }),
    shareReplay(1)
  );

  readonly tasks$: Observable<dynamicCRMModels.ICrmTask[]> = combineLatest([
    this.tasksFromCrm$,
    this.taskSortOrder$
  ]).pipe(
    map(([tasks, sortOrder]) => this.dynamiccrmSvc.sortTasks(tasks, sortOrder)),
    tap(tasks => {
      this.renderedTasks = tasks;
      this.emitTasksCount(tasks.length);
      this.scheduleTaskOverflowDetection();
    }),
    shareReplay(1)
  );

  constructor(
    private dynamiccrmSvc: DynamicCrmService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Tasks load only when the tasks tab is clicked.
  }

  ngAfterViewInit(): void {
    this.taskBodyEls?.changes.subscribe(() => this.scheduleTaskOverflowDetection());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partyDetails']) {
      this.hasPendingPartyRefresh = true;

      if (!changes['partyDetails'].currentValue?.length) {
        this.profileIds$.next([]);
        this.hasRequestedTasks = false;
        this.tasksCountChange.emit(0);
      }

      if (changes['partyDetails'].currentValue?.length && this.hasRequestedTasks) {
        this.refreshTasks();
        this.hasPendingPartyRefresh = false;
      }
    }
  }

  getTasks(): void {
    if (!this.partyDetails.length) {
      this.profileIds$.next([]);
      this.hasRequestedTasks = false;
      this.tasksCountChange.emit(0);
      return;
    }

    if (!this.hasRequestedTasks || this.hasPendingPartyRefresh) {
      this.refreshTasks();
      this.hasPendingPartyRefresh = false;
    }
  }

  onTaskTimeRangeChange(value: dynamicCRMModels.TaskTimeRange): void {
    this.taskTimeRange$.next(value);
  }

  toggleTaskSort(): void {
    const nextOrder: dynamicCRMModels.TaskSortOrder = this.taskSortOrder$.value === 'desc' ? 'asc' : 'desc';
    this.taskSortOrder$.next(nextOrder);
  }

  toggleCreateTask(): void {
    this.isCreateTaskOpen = !this.isCreateTaskOpen;
    if (this.isCreateTaskOpen) {
      this.createTaskForm.patchValue({
        taskPriority: 1,
        taskDuration: 30,
        taskDueTime: '08:00',
        taskStatus: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED,
        taskSendEmail: false
      });
    } else {
      this.resetCreateTaskForm();
    }
  }

  openCreateTask(): void {
    if (!this.isCreateTaskOpen) {
      this.toggleCreateTask();
    }
  }

  resetCreateTaskForm(): void {
    this.createTaskForm.reset({
      taskSubject: '',
      taskDescription: '',
      taskDueDate: null,
      taskDueTime: '08:00',
      taskPriority: 1,
      taskDuration: 30,
      taskStatus: dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED,
      taskSendEmail: false
    });

    this.createTaskErrorMessage = '';
    this.createTaskSuccessMessage = '';
    this.selectedTaskAttachmentFile = null;
    this.selectedTaskAttachmentName = '';
    this.selectedTaskAttachmentSize = 0;
  }

  onTaskAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    if (!file) {
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size === 0) {
      this.createTaskErrorMessage = 'Attachment cannot be empty.';
      input.value = '';
      return;
    }
    if (file.size > maxSize) {
      this.createTaskErrorMessage = 'Attachment must be 5 MB or less.';
      input.value = '';
      return;
    }

    this.selectedTaskAttachmentFile = file;
    this.selectedTaskAttachmentName = file.name;
    this.selectedTaskAttachmentSize = file.size;
    this.createTaskErrorMessage = '';
  }

  removeTaskAttachment(input?: HTMLInputElement): void {
    this.selectedTaskAttachmentFile = null;
    this.selectedTaskAttachmentName = '';
    this.selectedTaskAttachmentSize = 0;
    if (input) {
      input.value = '';
    }
  }

  formatAttachmentSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  onTaskDescriptionChanged(event: { html: string; editor: any }): void {
    this.createTaskForm.get('taskDescription')?.setValue(event.html);
    this.createTaskForm.get('taskDescription')?.markAsTouched();
  }

  sanitizeTaskDescription(html: string | undefined): SafeHtml {
    if (!html) {
      return '';
    }
    return sanitizeTrustedHtml(this.sanitizer, html);
  }

  toggleTaskExpand(task: dynamicCRMModels.ICrmTask): void {
    task.isExpanded = !task.isExpanded;
  }

  // Runs after layout so the clamped height is measurable; only shows "See more" when the text overflows.
  private scheduleTaskOverflowDetection(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => this.detectTaskOverflow()));
  }

  private detectTaskOverflow(): void {
    if (!this.taskBodyEls?.length) {
      return;
    }
    this.taskBodyEls.forEach((ref, idx) => {
      const task = this.renderedTasks[idx];
      if (!task || task.isExpanded) {
        return;
      }
      const el = ref.nativeElement;
      task.showSeeMore = el.scrollHeight - el.clientHeight > 1;
    });
    this.cdr.detectChanges();
  }

  submitTask(): void {
    if (this.createTaskForm.invalid || this.isSubmittingTask) {
      this.createTaskForm.markAllAsTouched();
      return;
    }

    const formValue = this.createTaskForm.getRawValue();
    const selectedParties = this.partyDetails.filter(p => !!p.partyId);

    if (!selectedParties.length) {
      this.createTaskErrorMessage = 'No client data is available to create this task.';
      return;
    }

    this.isSubmittingTask = true;
    this.createTaskErrorMessage = '';
    this.createTaskSuccessMessage = '';

    console.debug('[TradeDetailsTasks] submitTask started', {
      selectedPartiesCount: selectedParties.length,
      hasAttachment: !!this.selectedTaskAttachmentFile,
      attachmentName: this.selectedTaskAttachmentFile?.name,
      attachmentSize: this.selectedTaskAttachmentFile?.size,
      formValue
    });

    const taskRequests = selectedParties.map(party =>
      this.dynamiccrmSvc.resolveCrmEntityByParty(party.partyId, party.partyType).pipe(
        first(),
        switchMap(resolved => {
          if (!resolved.crmId || !resolved.crmEntityType) {
            throw new Error('Could not resolve CRM contact/account for selected client.');
          }

          const payload: dynamicCRMModels.ICreateTaskPayload = {
            subject: formValue.taskSubject || '',
            description: formValue.taskDescription || undefined,
            prioritycode: formValue.taskPriority ?? 1,
            actualdurationminutes: formValue.taskDuration ?? 30,
            rcm_sendemail: !!formValue.taskSendEmail,
            statuscode: formValue.taskStatus ?? dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED,
            scheduledend: this.toDueDateIso(formValue.taskDueDate, formValue.taskDueTime)
          };

          console.debug('[TradeDetailsTasks] submitTask payload prepared', {
            partyId: party.partyId,
            partyType: party.partyType,
            crmId: resolved.crmId,
            crmEntityType: resolved.crmEntityType,
            payload
          });

          if (resolved.crmEntityType === 'account') {
            payload['regardingobjectid_account_task@odata.bind'] = `/accounts(${resolved.crmId})`;
          } else {
            payload['regardingobjectid_contact_task@odata.bind'] = `/contacts(${resolved.crmId})`;
          }

          return this.dynamiccrmSvc.createTask(payload).pipe(
            switchMap(response => {
              const taskId = response?.activityid as string | undefined;
              if (taskId && this.selectedTaskAttachmentFile) {
                return this.dynamiccrmSvc.uploadTaskAttachment(taskId, this.selectedTaskAttachmentFile).pipe(
                  map(() => response),
                  catchError(() => of(response))
                );
              }
              return of(response);
            })
          );
        })
      )
    );

    forkJoin(taskRequests).pipe(first()).subscribe({
      next: responses => {
        console.debug('[TradeDetailsTasks] submitTask success', {
          createdCount: responses?.length || 0,
          responses
        });
        this.isSubmittingTask = false;
        this.isCreateTaskOpen = false;
        this.resetCreateTaskForm();
        this.createTaskSuccessMessage = 'Task(s) created successfully.';
        this.refreshTasks();
      },
      error: error => {
        console.error('[TradeDetailsTasks] submitTask failed', {
          selectedParties,
          hasAttachment: !!this.selectedTaskAttachmentFile,
          error
        });
        this.isSubmittingTask = false;
        this.createTaskErrorMessage = 'Failed to create task. Please try again.';
      }
    });
  }

  downloadTaskAttachment(task: dynamicCRMModels.ICrmTask): void {
    if (!task.activityid || !task.filename) {
      return;
    }

    this.taskAttachmentErrorMessage = '';
    this.isDownloadingAttachment$.next(true);

    this.dynamiccrmSvc.downloadTaskAttachment(task.activityid)
      .pipe(
        first(),
        finalize(() => this.isDownloadingAttachment$.next(false))
      )
      .subscribe({
        next: data => {
          if (!data?.length) {
            this.taskAttachmentErrorMessage = 'No attachment found for this task.';
            return;
          }

          const safeBytes = new Uint8Array(data.length);
          safeBytes.set(data);
          const blob = new Blob([safeBytes.buffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = task.filename || 'task-attachment';
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          this.taskAttachmentErrorMessage = 'Failed to download attachment.';
        }
      });
  }

  getTaskPriorityLabel(task: dynamicCRMModels.ICrmTask): string {
    switch (task.prioritycode) {
      case 0:
        return 'Low';
      case 2:
        return 'High';
      default:
        return 'Normal';
    }
  }

  getTaskStatusLabel(task: dynamicCRMModels.ICrmTask): string {
    if (task.statecode === 1) {
      return 'Completed';
    }

    if (task.statecode === 2) {
      return 'Cancelled';
    }

    switch (task.statuscode) {
      case dynamicCRMModels.INDIVIDUAL_TASK_STATUS.NOT_STARTED:
        return 'Not Started';
      case dynamicCRMModels.INDIVIDUAL_TASK_STATUS.IN_PROGRESS:
        return 'In Progress';
      case dynamicCRMModels.INDIVIDUAL_TASK_STATUS.WAITING:
        return 'Waiting on Someone Else';
      case dynamicCRMModels.INDIVIDUAL_TASK_STATUS.DEFERRED:
        return 'Deferred';
      default:
        return 'Open';
    }
  }

  getTaskInitials(ownerName: string | undefined): string {
    if (!ownerName?.trim()) {
      return '?';
    }

    const parts = ownerName.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }

  getTaskRegardingName(task: dynamicCRMModels.ICrmTask): string {
    return task.contactFullName || task.accountName || '';
  }

  formatTaskDate(isoDate: string | undefined): string {
    if (!isoDate) {
      return '';
    }

    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        + ' '
        + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return isoDate;
    }
  }

  closeTask(task: dynamicCRMModels.ICrmTask): void {
    if (!task.activityid || this.closingTaskId) {
      return;
    }

    this.closingTaskId = task.activityid;
    this.tasksErrorMessage = '';

    this.dynamiccrmSvc.closeTask(task.activityid).pipe(first()).subscribe({
      next: () => {
        this.closingTaskId = '';
        this.refreshTasks();
      },
      error: () => {
        this.closingTaskId = '';
        this.tasksErrorMessage = 'Unable to close task.';
      }
    });
  }

  isTaskCompleted(task: dynamicCRMModels.ICrmTask): boolean {
    return task.statecode === 1 || task.statuscode === dynamicCRMModels.INDIVIDUAL_TASK_STATUS.COMPLETED;
  }

  private emitTasksCount(count: number): void {
    this.tasksCountChange.emit(count);
  }

  private resolveSharedProfiles(): void {
    const partiesForLookup = this.partyDetails.filter(p => !!p.partyId);
    if (!partiesForLookup.length) {
      this.hasResolvedProfiles = true;
      this.profileIds$.next([]);
      return;
    }

    this.hasResolvedProfiles = false;
    const resolvers = partiesForLookup.map(p => this.dynamiccrmSvc.resolveCrmEntityByParty(p.partyId, p.partyType || '').pipe(first()));

    forkJoin(resolvers).subscribe({
      next: results => {
        this.hasResolvedProfiles = true;
        const profileIds = results
          .map(r => r.crmId)
          .filter((id): id is string => !!id);
        this.profileIds$.next(profileIds);
      },
      error: () => {
        this.hasResolvedProfiles = true;
        this.profileIds$.next([]);
        this.tasksErrorMessage = 'Unable to resolve CRM profiles.';
      }
    });
  }

  private refreshTasks(): void {
    this.hasRequestedTasks = true;
    this.tasksErrorMessage = '';
    this.resolveSharedProfiles();
  }

  private toDueDateIso(date: Date | null, time: string | null): string | undefined {
    if (!date) {
      return undefined;
    }

    const result = new Date(date);
    const [hoursStr, minutesStr] = (time || '08:00').split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    result.setHours(Number.isFinite(hours) ? hours : 8, Number.isFinite(minutes) ? minutes : 0, 0, 0);
    return result.toISOString();
  }
}
